import React, {
  useCallback, useEffect, useRef, useState,
} from "react";

import { Button } from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import { SPEC_FLAGS_MAP } from "../../../../Common/constants/SpecFlagConstants";

import { getPanelReportSpecDetail } from "../../../../../actions/reports";

import BackArrowIcon from "../../../../../assets/images/backArrowIcon.png";
import { ReactComponent as LoadingDots } from "../../../../../assets/images/common/LoadingDots.svg";

import "./DetailedPanelReport.css";
import "./DetailedPesticideReport.css";
import "./SampleTestResultTable.css";

const getSubtestsFromResponse = (response) => {
  const result = response?.result;
  if (Array.isArray(result?.subtests)) return result.subtests;
  if (Array.isArray(result)) return result;
  if (Array.isArray(response?.subtests)) return response.subtests;
  return [];
};

const getResultColor = (testSpecFlag) => {
  if (testSpecFlag === undefined || testSpecFlag === null || testSpecFlag === "" || testSpecFlag === "-") {
    return SPEC_FLAGS_MAP.invalid.color;
  }
  const flag = SPEC_FLAGS_MAP[testSpecFlag] || SPEC_FLAGS_MAP.invalid;
  return flag.color;
};

function DetailedPanelReport({
  onClose,
  reportData,
  activeDate,
  panelDetailTest,
  oldestDate,
}) {
  const [loadingData, setLoadingData] = useState(true);
  const [components, setComponents] = useState([]);
  const isMounted = useRef(true);

  const fetchPanelDetail = useCallback(async () => {
    setLoadingData(true);
    const { sample_id, sample_type, received_date: receivedDate } = reportData;
    const received_date = moment(new Date(receivedDate)).format("YYYY/MM/DD");
    const fActiveDate = moment(new Date(activeDate)).format("YYYY/MM/DD");
    const oldest = moment(new Date(oldestDate)).format("YYYY/MM/DD");
    const retest_date = fActiveDate !== oldest ? fActiveDate : null;

    try {
      const response = await getPanelReportSpecDetail({
        sample_id, sample_type, received_date, retest_date, test: panelDetailTest,
      });
      if (!isMounted.current) return;

      if (response.success) {
        setComponents(getSubtestsFromResponse(response));
      } else {
        setComponents([]);
        toast.error(response.message);
      }
    } catch (e) {
      if (!isMounted.current) return;
      setComponents([]);
      toast.error("Unable to load panel report details.");
    } finally {
      if (isMounted.current) {
        setLoadingData(false);
      }
    }
  }, [reportData, activeDate, panelDetailTest, oldestDate]);

  useEffect(() => {
    isMounted.current = true;
    fetchPanelDetail();
    return () => {
      isMounted.current = false;
    };
  }, [fetchPanelDetail]);

  if (loadingData) {
    return (
      <div className="DetailedPesticideReport__Loading">
        <LoadingDots width={28} height={28} fill="#19305A" />
      </div>
    );
  }

  return (
    <>
      <div className="DetailedPesticideReport__ReportHeader">
        <Button htmlType="button" onClick={onClose} className="DetailedPesticideReport__BackButton">
          <img src={BackArrowIcon} alt="Back Arrow" />
        </Button>
        <span className="SampleTestResultTable__PesticideDetailTitle">
          {`Detailed ${panelDetailTest || ""}`.trim()}
        </span>
      </div>
      <div className="SampleTestResultTable__Container">
        <table className="SampleTestResultTable">
          <thead className="sampleTestResultTableHeader">
            <tr>
              <th className="DetailedPanelReport__HeaderCell">Test</th>
              <th className="DetailedPanelReport__HeaderCell TruncateText" title="Specs">Specs</th>
              <th className="DetailedPanelReport__HeaderCell">Results</th>
            </tr>
          </thead>
          <tbody>
            {components.map((component) => {
              const specValue = component.test_spec || "-";
              const resultValue = component.test_result || "-";
              return (
                <tr key={component.test}>
                  <td>
                    <div className="sampleTestResultTableTestColumn">
                      <div
                        className="sampleTestResultTableTestText"
                        title={component.test}
                      >
                        {component.test}
                      </div>
                    </div>
                  </td>
                  <td title={specValue} className="TruncateText">{specValue}</td>
                  <td style={{ color: getResultColor(component.test_spec_flag) }}>
                    <div
                      className="sampleTestResultTableTestColumn"
                      title={resultValue}
                    >
                      <div className="ResultBox">{resultValue}</div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default DetailedPanelReport;
